import React, { useEffect } from 'react';
import * as d3 from 'd3';
import { useChartDimensions } from '../hooks/useChartDimensions';

/**
 * Bar chart — vertical or horizontal.
 */
export default function BarChart({
  data,
  xKey,
  yKey,
  color = 'var(--blue)',
  horizontal = false,
  yFormat = (v) => v.toLocaleString(),
  xFormat,
  tooltipFormat,
  heightRatio = 0.5,
  barLabel = false,
  onBarClick,
}) {
  const { containerRef, svgRef, tooltipRef, dimensions, hideTooltip } = useChartDimensions(
    (w) => Math.min(450, Math.max(260, w * heightRatio))
  );

  useEffect(() => {
    if (!data?.length || !dimensions.width) {
      hideTooltip();
      return;
    }
    const cleanData = data.filter((d) => d[xKey] != null && d[yKey] != null);
    if (!cleanData.length) {
      hideTooltip();
      return;
    }
    const { width, height } = dimensions;

    const longestLabel = d3.max(cleanData, (d) => String(d[xKey]).length) || 6;
    const margin = horizontal
      ? { top: 10, right: 50, bottom: 30, left: Math.min(220, 8 + longestLabel * 7) }
      : { top: 20, right: 20, bottom: 64, left: 70 };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    svg.attr('viewBox', `0 0 ${width} ${height}`);
    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);
    const tooltip = d3.select(tooltipRef.current);

    if (horizontal) {
      const y = d3.scaleBand().domain(cleanData.map((d) => d[xKey])).range([0, innerH]).padding(0.22);
      const maxVal = d3.max(cleanData, (d) => d[yKey]) || 1;
      const x = d3.scaleLinear().domain([0, maxVal * 1.1]).range([0, innerW]).nice();

      g.append('g').call(d3.axisBottom(x).ticks(5).tickSize(innerH).tickFormat(''))
        .selectAll('line').attr('stroke', '#E2E8F0').attr('stroke-dasharray', '3,3');
      g.selectAll('.domain').remove();

      g.selectAll('.bar').data(cleanData).enter().append('rect')
        .attr('y', (d) => y(d[xKey])).attr('height', y.bandwidth())
        .attr('x', 0).attr('width', (d) => x(d[yKey] || 0))
        .attr('fill', (d) => (typeof color === 'function' ? color(d) : color))
        .attr('rx', 3)
        .attr('cursor', onBarClick ? 'pointer' : 'default')
        .on('mouseenter', (event, d) => {
          tooltip.style('opacity', 1)
            .style('left', `${event.clientX + 12}px`).style('top', `${event.clientY - 10}px`)
            .html(tooltipFormat ? tooltipFormat(d) : `<div class="tip-label">${d[xKey]}</div><div><strong>${yFormat(d[yKey])}</strong></div>`);
        })
        .on('mousemove', (event) => tooltip.style('left', `${event.clientX + 12}px`).style('top', `${event.clientY - 10}px`))
        .on('mouseleave', hideTooltip)
        .on('click', (event, d) => {
          if (onBarClick) {
            event.stopPropagation();
            onBarClick(d);
          }
        });

      if (barLabel) {
        g.selectAll('.bar-label').data(cleanData).enter().append('text')
          .attr('x', (d) => x(d[yKey] || 0) + 5)
          .attr('y', (d) => y(d[xKey]) + y.bandwidth() / 2)
          .attr('dy', '0.35em').attr('font-size', '11px').attr('fill', '#475569')
          .attr('font-weight', '600')
          .text((d) => yFormat(d[yKey]));
      }

      g.append('g').call(d3.axisLeft(y).tickFormat(xFormat || ((d) => (String(d).length > 26 ? String(d).slice(0, 26) + '…' : d))))
        .selectAll('text').attr('font-size', '11px').attr('fill', '#475569');

      g.append('g').attr('transform', `translate(0,${innerH})`).call(d3.axisBottom(x).ticks(5).tickFormat(yFormat))
        .selectAll('text').attr('font-size', '11px').attr('fill', '#64748B');
    } else {
      const x = d3.scaleBand().domain(cleanData.map((d) => d[xKey])).range([0, innerW]).padding(0.22);
      const maxVal = d3.max(cleanData, (d) => d[yKey]) || 1;
      const y = d3.scaleLinear().domain([0, maxVal * 1.1]).range([innerH, 0]).nice();

      g.append('g').call(d3.axisLeft(y).ticks(5).tickSize(-innerW).tickFormat(''))
        .selectAll('line').attr('stroke', '#E2E8F0').attr('stroke-dasharray', '3,3');
      g.selectAll('.grid .domain').remove();

      g.selectAll('.bar').data(cleanData).enter().append('rect')
        .attr('x', (d) => x(d[xKey])).attr('width', x.bandwidth())
        .attr('y', (d) => y(d[yKey] || 0)).attr('height', (d) => innerH - y(d[yKey] || 0))
        .attr('fill', (d) => (typeof color === 'function' ? color(d) : color))
        .attr('rx', 3)
        .attr('cursor', onBarClick ? 'pointer' : 'default')
        .on('mouseenter', (event, d) => {
          tooltip.style('opacity', 1)
            .style('left', `${event.clientX + 12}px`).style('top', `${event.clientY - 10}px`)
            .html(tooltipFormat ? tooltipFormat(d) : `<div class="tip-label">${d[xKey]}</div><div><strong>${yFormat(d[yKey])}</strong></div>`);
        })
        .on('mousemove', (event) => tooltip.style('left', `${event.clientX + 12}px`).style('top', `${event.clientY - 10}px`))
        .on('mouseleave', hideTooltip)
        .on('click', (event, d) => {
          if (onBarClick) {
            event.stopPropagation();
            onBarClick(d);
          }
        });

      if (barLabel) {
        g.selectAll('.bar-label').data(cleanData).enter().append('text')
          .attr('x', (d) => x(d[xKey]) + x.bandwidth() / 2)
          .attr('y', (d) => y(d[yKey] || 0) - 5)
          .attr('text-anchor', 'middle').attr('font-size', '11px').attr('fill', '#475569').attr('font-weight', '600')
          .text((d) => yFormat(d[yKey]));
      }

      g.append('g').attr('transform', `translate(0,${innerH})`)
        .call(d3.axisBottom(x).tickFormat(xFormat || ((d) => d)))
        .selectAll('text').attr('font-size', '11px').attr('fill', '#64748B')
        .attr('transform', 'rotate(-32)').attr('text-anchor', 'end');

      g.append('g').call(d3.axisLeft(y).ticks(5).tickFormat(yFormat))
        .selectAll('text').attr('font-size', '11px').attr('fill', '#64748B');
    }

    g.selectAll('.domain').attr('stroke', '#CBD5E1');
    g.selectAll('.tick line').attr('stroke', '#CBD5E1');
    return hideTooltip;
  }, [data, dimensions, xKey, yKey, color, horizontal, yFormat, xFormat, tooltipFormat, barLabel, onBarClick, hideTooltip]);

  if (!data?.length) return <div className="chart-container"><div className="no-data">No data</div></div>;
  return (
    <div className="chart-container" ref={containerRef}>
      <svg ref={svgRef} />
      <div className="d3-tooltip" ref={tooltipRef} style={{ opacity: 0 }} />
    </div>
  );
}
